// project-imports
import useCommonStyles from 'helpers/commonStyles';
import { getFormattedDate, getLocalDateTimeFields } from 'helpers/dates';
import { capitalizeString } from 'helpers/stringFunctions';
import { dispatch, useSelector } from 'store';
import { rowsPerPagePaginationAlt } from 'store/constant';
import { getBusinessDocuments } from 'store/slices/memberPriority';
import MainCard from 'ui-component/cards/MainCard';
import { businessDocumentsHeadCells } from '../../constant';

// material-ui
import {
    CircularProgress,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

// third-party
import PropTypes from 'prop-types';

const TableHeading = () => (
    <>
        {businessDocumentsHeadCells.map((item) => (
            <TableCell align={item.align}>{item?.label}</TableCell>
        ))}
    </>
);

const BusinessDocuments = ({ organizationCode }) => {
    const theme = useTheme();
    const commonClasses = useCommonStyles();

    const memberPriorityData = useSelector((state) => state?.memberPriority);
    const { businessDocuments = {}, isBusinessDocsLoading = true } = memberPriorityData || {};
    const {
        results = [],
        count = 0,
        next = null,
        previous = null,
        page_size: rowsPerPage = rowsPerPagePaginationAlt,
        currentPage = 0
    } = businessDocuments || {};

    const redirectToUrl = (url = '') => {
        window.open(url, '_blank');
    };

    const BUSINESS_DOCS_STATUS = {
        approved: { label: 'Approved', color: theme.palette.success.dark },
        pending: { label: 'Pending', color: theme.palette.warning.dark },
        expired: { label: 'Expired', color: theme.palette.grey.primary },
        rejected: { label: 'Rejected', color: theme.palette.orange.dark }
    };

    const handleChangePage = (event, nextPage) => {
        if (nextPage - currentPage > 0) {
            // next
            dispatch(getBusinessDocuments(organizationCode, next, nextPage));
        } else if (nextPage - currentPage < 0) {
            // previous
            dispatch(getBusinessDocuments(organizationCode, previous, nextPage));
        }
    };

    return (
        <Grid item xs={12}>
            <MainCard content={false}>
                <TableContainer>
                    <Table aria-label="simple table">
                        <TableHead>
                            <TableRow className={commonClasses.primaryBackground}>
                                <TableHeading />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isBusinessDocsLoading ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : (
                                results.map((item) => {
                                    const {
                                        document_type: documentType = {},
                                        current_status: currentStatus = {},
                                        document = '',
                                        document2 = '',
                                        created_by: createdBy = {},
                                        created_at: createdAt = ''
                                    } = item || {};

                                    const { status = '', reason = '', comment = '' } = currentStatus || {};
                                    const { display_name: displayName = '' } = documentType || {};

                                    const { hours24: lastReachHrs = '', minutes: lastReachMin = '' } =
                                        getLocalDateTimeFields(createdAt) || {};

                                    return (
                                        <>
                                            <TableRow hover>
                                                <TableCell sx={{ minWidth: '130px' }}>{displayName || '-'}</TableCell>
                                                <TableCell
                                                    sx={{
                                                        minWidth: '170px',
                                                        cursor: 'pointer',
                                                        color: document ? theme.palette.primary.dark : {},
                                                        fontWeight: 500
                                                    }}
                                                    component="th"
                                                    onClick={() => redirectToUrl(document)}
                                                >
                                                    {document ? `${displayName} front` : '-'}
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        minWidth: '170px',
                                                        cursor: 'pointer',
                                                        color: document2 ? theme.palette.primary.dark : {},
                                                        fontWeight: 500
                                                    }}
                                                    component="th"
                                                    onClick={() => redirectToUrl(document2)}
                                                >
                                                    {document2 ? `${displayName} back` : '-'}
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        color: BUSINESS_DOCS_STATUS[status.toLowerCase()] || theme.palette.grey.primary,
                                                        minWidth: '120px',
                                                        fontWeight: 500
                                                    }}
                                                >
                                                    {BUSINESS_DOCS_STATUS[status]?.label || capitalizeString(status, ' ') || '-'}
                                                </TableCell>
                                                <TableCell sx={{ minWidth: '120px' }}>{reason || '-'}</TableCell>
                                                <TableCell sx={{ minWidth: '140px' }}>{comment || '-'}</TableCell>
                                                <TableCell sx={{ minWidth: '120px' }}>{createdBy?.username || '-'}</TableCell>
                                                <TableCell
                                                    sx={{
                                                        minWidth: '140px'
                                                    }}
                                                    component="th"
                                                >
                                                    {createdAt ? (
                                                        <>
                                                            <Typography>{`${getFormattedDate(createdAt)}`}</Typography>
                                                            <Typography
                                                                variant="h6"
                                                                sx={{ color: theme.palette.grey[500], fontWeight: 400 }}
                                                            >
                                                                {`${lastReachHrs}:${lastReachMin}`}
                                                            </Typography>
                                                        </>
                                                    ) : (
                                                        '-'
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        </>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[rowsPerPage]}
                    component="div"
                    rowsPerPage={rowsPerPage}
                    page={currentPage}
                    count={parseInt(count, 10)}
                    backIconButtonProps={{
                        'aria-label': 'previous page'
                    }}
                    nextIconButtonProps={{
                        'aria-label': 'next page'
                    }}
                    onPageChange={handleChangePage}
                    labelDisplayedRows={({ count }) => `Page ${currentPage + 1} of ${Math.ceil(parseInt(count, 10) / rowsPerPage)}`}
                    sx={{ '& .MuiTablePagination-toolbar': { padding: 0 } }}
                />
            </MainCard>
        </Grid>
    );
};

BusinessDocuments.propTypes = {
    organizationCode: PropTypes.string
};

export default BusinessDocuments;
